import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import "./Menu.css";
import logo from "../../Pictuers/UniTech_PNG.png";
import { UilGlobe } from "@iconscout/react-unicons";
import { useTranslation } from "react-i18next";

/* ==== Drapeaux en SVG inline (compatibles Windows/macOS) ====        HOST=0.0.0.0 PORT=3001 npm start */
const FlagFR = ({ className, title = "Drapeau Français" }) => (
  <svg
    viewBox="0 0 3 2"
    width="16"
    height="12"
    className={className}
    role="img"
    aria-label={title}
  >
    <rect width="1" height="2" x="0" y="0" fill="#0055A4" />
    <rect width="1" height="2" x="1" y="0" fill="#FFFFFF" />
    <rect width="1" height="2" x="2" y="0" fill="#EF4135" />
  </svg>
);

const FlagGB = ({ className, title = "Flag United Kingdom" }) => (
  <svg
    viewBox="0 0 60 30"
    width="16"
    height="12"
    className={className}
    role="img"
    aria-label={title}
  >
    <clipPath id="gb-clip">
      <rect x="0" y="0" width="60" height="30" rx="1.5" ry="1.5" />
    </clipPath>
    <g clipPath="url(#gb-clip)">
      <path fill="#012169" d="M0 0h60v30H0z" />
      <path
        fill="#FFF"
        d="M0 0l60 30M60 0L0 30"
        stroke="#FFF"
        strokeWidth="6"
      />
      <path
        d="M0 0l60 30M60 0L0 30"
        stroke="#C8102E"
        strokeWidth="4"
      />
      <path fill="#FFF" d="M25 0h10v30H25zM0 10h60v10H0z" />
      <path fill="#C8102E" d="M27 0h6v30h-6zM0 12h60v6H0z" />
    </g>
  </svg>
);

// Navigation temporaire : false = lien direct OPENlift (ISS masqué du menu).
// Repasser à true pour retrouver le sous-menu Solutions d'origine,
// lorsque la Téléalarme ISS sera prête à être présentée.
const SHOW_SOLUTIONS_MENU = false;

export default function Menu() {
  const location = useLocation();
  const { t, i18n } = useTranslation();

  // ↓ Pages sur lesquelles on veut le motif/ background comme sur l'accueil
  const patternPaths = [
    /^\/$/,                                  // Accueil
    /^\/openlift(?:\/|$)/,                   // Page OPENlift
    ///^\/produits\/telealarme-iss(?:\/|$)/,   // Téléalarme ISS
    /^\/produits\/passerelle-iss(?:\/|$)/,   // Passerelle ISS
    /^\/produits\/kube(?:\/|$)/,             // KUBE
    /^\/nous-sommes(?:\/|$)/,                // Nous Sommes
  ];
  const isPatternPage = patternPaths.some((re) => re.test(location.pathname));
  const isHome = location.pathname === "/";
  const isOpenliftPage = /^\/openlift(?:\/|$)/.test(location.pathname);
  const isISSPage = /^\/produits\/telealarme-iss(?:\/|$)/.test(location.pathname);
  // Même apparence premium à fond clair pour OPENlift et Téléalarme ISS.
  // Réutilise la classe CSS déjà validée, sans retoucher les autres pages.
  const isPremiumLightPage = isOpenliftPage || isISSPage || /^\/nous-sommes(?:\/|$)/.test(location.pathname) || /^\/contact(?:\/|$)/.test(location.pathname) || /^\/blog(?:\/|$)/.test(location.pathname) || /^\/demo(?:\/|$)/.test(location.pathname);

  // Accueil : la vidéo du Hero reste visible derrière la navigation.
  // Un fond opaque n'apparait que lorsque le Hero quitte la zone du menu.
  const [homeNavScrolled, setHomeNavScrolled] = useState(false);
  useEffect(() => {
    if (!isHome) {
      setHomeNavScrolled(false);
      return undefined;
    }

    const updateScrollState = () => {
      const hero = document.querySelector(".page-accueil .hero.hero--v3");
      const header = document.querySelector("header.topbar--home-premium");
      const navHeight = header ? header.getBoundingClientRect().height : 78;

      // Utiliser la position reelle du Hero : fiable sur PC et sur mobile.
      setHomeNavScrolled(
        hero ? hero.getBoundingClientRect().bottom <= navHeight : false
      );
    };

    updateScrollState();
    window.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);

    return () => {
      window.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [isHome]);

  /* ====== État desktop (dropdown Produits) ====== */
  const [prodOpen, setProdOpen] = useState(false);
  const prodRef = useRef(null);
  const closeTimerRef = useRef(null);

  /* ====== Sélecteur de langue (commun) ====== */
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);
  const langCloseTimerRef = useRef(null);

  // État local de la langue (compatible avec ta clé localStorage existante)
  const [lang, setLang] = useState(() => {
    const saved = typeof window !== "undefined" ? window.localStorage.getItem("site-lang") : null;
    if (saved === "fr" || saved === "en") return saved;
    const nav = typeof navigator !== "undefined" ? navigator.language || navigator.userLanguage : "fr";
    return nav?.toLowerCase().startsWith("fr") ? "fr" : "en";
  });

  // Persistance inchangée
  useEffect(() => {
    try { window.localStorage.setItem("site-lang", lang); } catch {}
  }, [lang]);

  // Synchronise l'état local -> i18next
  useEffect(() => {
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  // Synchronise i18next -> état local (si une autre partie de l'app change la langue)
  useEffect(() => {
    const onChange = (lng) => setLang(lng);
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, [i18n]);

  useEffect(() => {
    function onDocClick(e) {
      if (prodRef.current && !prodRef.current.contains(e.target)) setProdOpen(false);
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    }
    function onKey(e) {
      if (e.key === "Escape") {
        setProdOpen(false);
        setLangOpen(false);
        setMobileOpen(false);
        setMobProdOpen(false);
        setMobLangOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  useEffect(() => {
    setProdOpen(false);
    setLangOpen(false);
    setMobileOpen(false);
    setMobProdOpen(false);
    setMobLangOpen(false);
    document.documentElement.classList.remove("no-scroll");
  }, [location.pathname]);

  const openNow = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setProdOpen(true);
  };
  const closeWithDelay = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setProdOpen(false), 200);
  };

  const openLangNow = () => {
    if (langCloseTimerRef.current) clearTimeout(langCloseTimerRef.current);
    setLangOpen(true);
  };
  const closeLangWithDelay = () => {
    if (langCloseTimerRef.current) clearTimeout(langCloseTimerRef.current);
    langCloseTimerRef.current = setTimeout(() => setLangOpen(false), 200);
  };

  const applyLang = (code) => {
    setLang(code);
    setLangOpen(false);
    setMobLangOpen(false);
  };

  const label = lang === "fr" ? t("lang.fr") : t("lang.en");
  // Libellés du nouveau menu, indépendants des anciennes traductions "Produits" / "Nous sommes".
  const solutionsLabel = "Solutions";
  const aboutLabel = lang === "fr" ? "À propos" : "About";
  const issLabel = lang === "fr" ? "Téléalarme ISS" : "ISS Telealarm";
  const solutionsRouteActive = location.pathname === "/openlift" ||
    location.pathname.startsWith("/openlift/") ||
    location.pathname === "/produits/telealarme-iss" ||
    location.pathname.startsWith("/produits/telealarme-iss/");

  /* ====== Panneau mobile ====== */
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobProdOpen, setMobProdOpen] = useState(false);
  const [mobLangOpen, setMobLangOpen] = useState(false);

  useEffect(() => {
    if (mobileOpen) document.documentElement.classList.add("no-scroll");
    else document.documentElement.classList.remove("no-scroll");
  }, [mobileOpen]);

  const closeMobile = () => {
    setMobileOpen(false);
    setMobProdOpen(false);
    setMobLangOpen(false);
  };

  const onMobileProduitsClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setMobProdOpen((v) => !v);
  };

  const onMobileLangClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setMobLangOpen((v) => !v);
  };

  const onDesktopProduitsClick = (e) => {
    e.preventDefault();
    setProdOpen((v) => !v);
  };

  return (
    <header
      className={`topbar ${isPatternPage ? "topbar--pattern" : "topbar--plain"} ${isHome ? "topbar--home-premium" : ""} ${isPremiumLightPage ? "topbar--openlift-premium" : ""} ${isHome && homeNavScrolled ? "topbar--home-scrolled" : ""}`}
      role="banner"
    >
      <div className="nav-outer">
        <nav className="nav-shell" role="navigation" aria-label={t("aria.main_menu")}>
          {/* Logo */}
          <a href="/" className="brand" aria-label={t("aria.brand_home")}>
            <img src={logo} alt="UniTech" className="brand-sq" />
          </a>

          {/* Liens (DESKTOP) — navigation principale */}
          <ul className="nav">
            <li>
              <NavLink to="/" end className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>
                {t("nav.home")}
              </NavLink>
            </li>

            {/* OPENlift : lien direct tant que le menu Solutions est masqué. */}
            {SHOW_SOLUTIONS_MENU ? (
              <>
            {/* Solutions : ouvre uniquement le sous-menu, sans navigation */}
            <li
              className={`has-dropdown ${prodOpen ? "open" : ""}`}
              ref={prodRef}
              onMouseEnter={openNow}
              onMouseLeave={closeWithDelay}
            >
              <button
                type="button"
                className={`nav-link nav-link--with-caret solutions-trigger${solutionsRouteActive ? " active" : ""}`}
                aria-haspopup="menu"
                aria-expanded={prodOpen ? "true" : "false"}
                aria-controls="submenu-solutions"
                onClick={onDesktopProduitsClick}
              >
                {solutionsLabel}
                <svg className="caret" width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              <ul id="submenu-solutions" className="dropdown" role="menu" aria-label={solutionsLabel}>
                <li role="none">
                  <NavLink to="/openlift" className="dropdown-link" role="menuitem" onClick={() => setProdOpen(false)}>
                    OPENlift
                  </NavLink>
                </li>
                <li role="none">
                  <NavLink to="/produits/telealarme-iss" className="dropdown-link" role="menuitem" onClick={() => setProdOpen(false)}>
                    {issLabel}
                  </NavLink>
                </li>
              </ul>
            </li>

              </>
            ) : (
              <li>
                <NavLink to="/openlift" className={({ isActive }) => "nav-link" + (isActive ? " active" : "") }>
                  OPENlift
                </NavLink>
              </li>
            )}

            <li>
              <NavLink to="/nous-sommes" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>
                {aboutLabel}
              </NavLink>
            </li>
            <li>
              <NavLink to="/blog" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>
                {t("nav.blog")}
              </NavLink>
            </li>
            <li>
              <NavLink to="/contact" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>
                {t("nav.contact")}
              </NavLink>
            </li>

            {/* Sélecteur de langue (DESKTOP) */}
            <li
              className={`has-dropdown nav-lang ${langOpen ? "open" : ""}`}
              ref={langRef}
              onMouseEnter={openLangNow}
              onMouseLeave={closeLangWithDelay}
            >
              <button
                type="button"
                className="nav-link nav-link--with-caret lang-btn"
                aria-haspopup="menu"
                aria-expanded={langOpen ? "true" : "false"}
                aria-controls="submenu-lang"
                onClick={() => setLangOpen(v => !v)}
              >
                <UilGlobe size="18" aria-hidden="true" />
                {lang === "fr" ? <FlagFR className="flag-img" /> : <FlagGB className="flag-img" />}
                <span className="lang-code">{label}</span>
                <svg className="caret" width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              <ul id="submenu-lang" className="dropdown lang-dropdown" role="menu" aria-label={t("aria.choose_language")}>
                {lang !== "fr" && (
                  <li role="none">
                    <button className="dropdown-link lang-option" role="menuitem" onClick={() => applyLang("fr")}>
                      <FlagFR className="flag-img" />
                      <span>{t("lang.fr")}</span>
                    </button>
                  </li>
                )}
                {lang !== "en" && (
                  <li role="none">
                    <button className="dropdown-link lang-option" role="menuitem" onClick={() => applyLang("en")}>
                      <FlagGB className="flag-img" />
                      <span>{t("lang.en")}</span>
                    </button>
                  </li>
                )}
              </ul>
            </li>

            {/* CTA DEMO (pill, à droite) */}
            <li className="nav-cta">
              <NavLink
                to="/demo"
                className="btn-cta"
                aria-label={`${t("nav.demo")} – ${t("nav.demo_badge")}`}
              >
                <span className="cta-label">{t("nav.demo")}</span>
                <span className="cta-badge" aria-hidden="true">{t("nav.demo_badge")}</span>
              </NavLink>
            </li>
          </ul>

          {/* BOUTON HAMBURGER (visible ≤ 1024px) */}
          <button
            className={`hamburger ${mobileOpen ? "is-active" : ""}`}
            aria-label={t("aria.open_menu")}
            aria-expanded={mobileOpen ? "true" : "false"}
            onClick={() => setMobileOpen(v => !v)}
          >
            <span className="hamburger-box">
              <span className="hamburger-inner"></span>
            </span>
          </button>
        </nav>
      </div>

      {/* PANNEAU MOBILE (aligné au container, backdrop transparent) */}
      <div className={`mobile-menu ${mobileOpen ? "open" : ""}`} role="dialog" aria-modal="true">
        <button className="mobile-menu__backdrop" aria-label={t("aria.close")} onClick={closeMobile} />
        <div className="mobile-menu__panel">
          <div className="mobile-menu__header" aria-hidden="true">
            <a href="/" className="brand" aria-label={t("aria.brand_home")}>
              <img src={logo} alt="" className="brand-sq" />
            </a>
            <button className="hamburger close-x" aria-label={t("aria.close")} onClick={closeMobile}>
              <span className="hamburger-box"><span className="hamburger-inner"></span></span>
            </button>
          </div>

          <ul className="mobile-nav" role="menu" aria-label={t("aria.mobile_nav") || "Navigation mobile"}>
            <li>
              <NavLink to="/" end className="mobile-link" role="menuitem">
                {t("nav.home")}
              </NavLink>
            </li>

            {/* OPENlift : lien direct sur le menu mobile également. */}
            {SHOW_SOLUTIONS_MENU ? (
              <>
            {/* Solutions : accordéon sans page de destination */}
            <li className={`mobile-accordion ${mobProdOpen ? "open" : ""}`}>
              <button
                type="button"
                className="mobile-link mobile-accordion__trigger solutions-mobile-trigger"
                onClick={onMobileProduitsClick}
                aria-expanded={mobProdOpen ? "true" : "false"}
                aria-controls="submenu-solutions-mobile"
              >
                <span>{solutionsLabel}</span>
                <svg className="caret" width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <ul id="submenu-solutions-mobile" className="mobile-submenu" role="menu" aria-label={solutionsLabel}>
                <li>
                  <NavLink to="/openlift" className="mobile-sublink" role="menuitem" onClick={closeMobile}>
                    OPENlift
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/produits/telealarme-iss" className="mobile-sublink" role="menuitem" onClick={closeMobile}>
                    {issLabel}
                  </NavLink>
                </li>
              </ul>
            </li>

              </>
            ) : (
              <li>
                <NavLink to="/openlift" className="mobile-link" role="menuitem" onClick={closeMobile}>
                  OPENlift
                </NavLink>
              </li>
            )}

            <li>
              <NavLink to="/nous-sommes" className="mobile-link" role="menuitem">{aboutLabel}</NavLink>
            </li>
            <li>
              <NavLink to="/blog" className="mobile-link" role="menuitem">{t("nav.blog")}</NavLink>
            </li>
            <li>
              <NavLink to="/contact" className="mobile-link" role="menuitem">{t("nav.contact")}</NavLink>
            </li>

            {/* Accordéon LANGUE */}
            <li className={`mobile-accordion ${mobLangOpen ? "open" : ""}`}>
              <a href="#lang" className="mobile-link mobile-accordion__trigger" onClick={onMobileLangClick} aria-expanded={mobLangOpen ? "true" : "false"}>
                <span className="mobile-lang-label">
                  <UilGlobe size="18" aria-hidden="true" />
                  {lang === "fr" ? <FlagFR className="flag-img" /> : <FlagGB className="flag-img" />}
                  <span className="lang-code">{label}</span>
                </span>
                <svg className="caret" width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
              <ul className="mobile-submenu" role="menu" aria-label={t("aria.choose_language")}>
                {lang !== "fr" && (
                  <li>
                    <button className="mobile-sublink" onClick={() => applyLang("fr")}>
                      <FlagFR className="flag-img" /> {t("lang.fr")}
                    </button>
                  </li>
                )}
                {lang !== "en" && (
                  <li>
                    <button className="mobile-sublink" onClick={() => applyLang("en")}>
                      <FlagGB className="flag-img" /> {t("lang.en")}
                    </button>
                  </li>
                )}
              </ul>
            </li>

            {/* CTA DEMO — identique desktop, centré en mobile */}
            <li className="mobile-cta">
              <NavLink to="/demo" className="btn-cta" role="menuitem" aria-label={`${t("nav.demo")} – ${t("nav.demo_badge")}`}>
                <span className="cta-label">{t("nav.demo")}</span>
                <span className="cta-badge" aria-hidden="true">1&nbsp;mois gratuit</span>
              </NavLink>
            </li>
          </ul>
        </div>
      </div>
    </header>
  );
}
